// src/main/MaintenanceRequest.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Avatar,
    Autocomplete,
    FormHelperText,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    Filter as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    Print,
    CheckCircle,
    People,
    AttachMoney,
    CloudUpload,
    History,
    Warning,
    Handyman,
    Pending,
    AssignmentInd,
    TrendingFlat,
    CheckCircleOutline,
    Plumbing,
    ElectricalServices,
    AcUnit,
    Kitchen,
    Construction,
    PestControl,
    Security,
    CleaningServices,
    MiscellaneousServices,
    ReportProblem,
    HighlightOff,
    Engineering,
    TrendingUp,
    TrendingDown,
    Event,
    Person,
    Business,
    Home,
    Description,
    Note,
    Receipt,
    MoneyOff,
    CloudDownload,
    LocalPrintshop,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIPropertyList,
    APITenantList,
    APIMaintenanceRequestList,
    APIMaintenanceRequestStore,
    APIMaintenanceRequestUpdate,
    APIMaintenanceRequestDelete,
    APIMaintenanceRequestExportExcel,
    APIMaintenanceRequestExportPDF,
    APIMaintenanceRequestPrint,
    APIMaintenanceRequestStats,
    APIMaintenanceRequestUpdateStatus,
    APIMaintenanceRequestAssign,
    APIMaintenanceRequestComplete,
    APIMaintenanceRequestDeductFromRent,
    APIMaintenanceRequestUploadPhoto,
    APIFloorUnitNames
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const MaintenanceRequest = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [statistics, setStatistics] = useState(null);
    const [properties, setProperties] = useState([]);
    const [apartments, setApartments] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState('');
    const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignedTo, setAssignedTo] = useState('');
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
    const [actualCost, setActualCost] = useState('');
    const [completionNotes, setCompletionNotes] = useState('');
    const [deductDialogOpen, setDeductDialogOpen] = useState(false);
    const [deductAmount, setDeductAmount] = useState('');
    const [deductNotes, setDeductNotes] = useState('');
    const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [requestHistory, setRequestHistory] = useState([]);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    
    // Search input states for autocomplete
    const [propertySearchInput, setPropertySearchInput] = useState('');
    const [apartmentSearchInput, setApartmentSearchInput] = useState('');

    // Use ref to track if component is mounted
    const isMounted = useRef(true);

    // Issue categories
    const issueCategories = [
        { id: 'plumbing', name: 'Plumbing', icon: <Plumbing />, color: 'info' },
        { id: 'electrical', name: 'Electrical', icon: <ElectricalServices />, color: 'warning' },
        { id: 'hvac', name: 'HVAC', icon: <AcUnit />, color: 'primary' },
        { id: 'appliance', name: 'Appliance', icon: <Kitchen />, color: 'secondary' },
        { id: 'structural', name: 'Structural', icon: <Construction />, color: 'error' },
        { id: 'pest', name: 'Pest Control', icon: <PestControl />, color: 'default' },
        { id: 'security', name: 'Security', icon: <Security />, color: 'info' },
        { id: 'cleaning', name: 'Cleaning', icon: <CleaningServices />, color: 'success' },
        { id: 'other', name: 'Other', icon: <MiscellaneousServices />, color: 'default' }
    ];

    // Request status options
    const requestStatusOptions = [
        { id: 'reported', name: 'Reported', color: 'error', icon: <ReportProblem />, step: 1 },
        { id: 'assigned', name: 'Assigned', color: 'info', icon: <AssignmentInd />, step: 2 },
        { id: 'in_progress', name: 'In Progress', color: 'warning', icon: <Engineering />, step: 3 },
        { id: 'completed', name: 'Completed', color: 'success', icon: <CheckCircle />, step: 4 },
        { id: 'cancelled', name: 'Cancelled', color: 'default', icon: <HighlightOff />, step: 5 }
    ];

    // Priority levels
    const priorityLevels = [
        { id: 'low', name: 'Low', color: 'info', icon: <TrendingDown /> },
        { id: 'medium', name: 'Medium', color: 'warning', icon: <TrendingFlat /> },
        { id: 'high', name: 'High', color: 'error', icon: <TrendingUp /> },
        { id: 'emergency', name: 'Emergency', color: 'error', icon: <Warning /> }
    ];

    // Form data for Maintenance Request
    const [formData, setFormData] = useState({
        floor_unit_name_id: '',
        tenant_id: '',
        property_id: '',
        floor_name: '',
        apartment_number: '',
        issue_category: '',
        issue_description: '',
        priority: 'medium',
        reported_date: new Date().toISOString().slice(0, 16),
        assigned_to: '',
        estimated_cost: '',
        actual_cost: '',
        deducted_from_rent: false,
        request_status: 'reported',
        notes: '',
        preferred_date: '',
        preferred_time: ''
    });

    const [filters, setFilters] = useState({
        request_status: '',
        priority: '',
        property_id: '',
        floor_unit_name_id: '',
        issue_category: '',
        date_from: '',
        date_to: '',
        estimated_cost_min: '',
        estimated_cost_max: ''
    });

    const [errors, setErrors] = useState({
        floor_unit_name_id: false,
        issue_description: false,
        reported_date: false
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [uploading, setUploading] = useState(false);

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchProperties();
        fetchTenants();
        fetchRequests();
        fetchStatistics();

        return () => {
            isMounted.current = false;
        };
    }, [showToast]);

    // Fetch properties for dropdown
    const fetchProperties = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIPropertyList);
            if (result.success && isMounted.current) {
                setProperties(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
        }
    };

    // Fetch apartments for selected property
    const fetchApartments = useCallback(async (propertyId = null) => {
        try {
            const userBranchId = getUserDetails("branchId");
            const params = {};
            if (propertyId) params.property_id = propertyId;
            
            const result = await apiService.list(userBranchId, APIFloorUnitNames, params);
            if (result.success && isMounted.current) {
                setApartments(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching apartments:", error);
            if (isMounted.current) setApartments([]);
        }
    }, []);

    // Fetch tenants
    const fetchTenants = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APITenantList);
            if (result.success && isMounted.current) {
                setTenants(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching tenants:", error);
            if (isMounted.current) setTenants([]);
        }
    };

    // Fetch maintenance requests
    const fetchRequests = async () => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIMaintenanceRequestList);

            if (result.success && isMounted.current) {
                const requests = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: requests
                }));
            } else if (isMounted.current) {
                setError(result.message);
            }
        } catch (error) {
            if (isMounted.current) {
                setError("Failed to load data");
            }
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APIMaintenanceRequestStats);
            if (result.success && result.data && isMounted.current) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    // Fetch request history
    const fetchRequestHistory = async (requestId) => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIMaintenanceRequestStats, { request_id: requestId });
            if (result.success && result.data && isMounted.current) {
                setRequestHistory(result.data.history || []);
            }
        } catch (error) {
            console.error("Error fetching history:", error);
            if (isMounted.current) setRequestHistory([]);
        }
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };
    
    const handlePrintRequest = async (record) => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print maintenance requests", "error");
            return;
        }
        
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));

            const result = await apiService.print(APIMaintenanceRequestPrint, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "PDF file generated successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to generate PDF file", "error");
            }
        }
    };
    
    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export maintenance data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToExcel(APIMaintenanceRequestExportExcel, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export Excel file", "error");
            }
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export maintenance data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToPDF(APIMaintenanceRequestExportPDF, formDataToSend);

            if (result && result.success && isMounted.current) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export PDF file", "error");
            }
        }
    };

    // Update request status
    const handleUpdateStatus = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update request status", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('request_id', selectedRecord.id);
            formDataToSend.append('request_status', newStatus);

            const result = await apiService.create(APIMaintenanceRequestUpdateStatus, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setStatusUpdateDialogOpen(false);
                setNewStatus('');
                fetchRequests();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Update status error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to update request status", "error");
            }
        }
    };

    // Assign request
    const handleAssign = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to assign requests", "error");
            return;
        }

        if (!assignedTo.trim()) {
            showToast("Validation Error", "Please enter who to assign to", "warning");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('request_id', selectedRecord.id);
            formDataToSend.append('assigned_to', assignedTo);

            const result = await apiService.create(APIMaintenanceRequestAssign, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setAssignDialogOpen(false);
                setAssignedTo('');
                fetchRequests();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Assign error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to assign request", "error");
            }
        }
    };

    // Complete request
    const handleComplete = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to complete requests", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('request_id', selectedRecord.id);
            formDataToSend.append('actual_cost', actualCost || 0);
            formDataToSend.append('completion_notes', completionNotes);

            const result = await apiService.create(APIMaintenanceRequestComplete, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setCompleteDialogOpen(false);
                setActualCost('');
                setCompletionNotes('');
                fetchRequests();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Complete error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to complete request", "error");
            }
        }
    };

    // Deduct from rent
    const handleDeductFromRent = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to deduct from rent", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('request_id', selectedRecord.id);
            formDataToSend.append('deduct_amount', deductAmount);
            formDataToSend.append('deduct_notes', deductNotes);

            const result = await apiService.create(APIMaintenanceRequestDeductFromRent, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setDeductDialogOpen(false);
                setDeductAmount('');
                setDeductNotes('');
                fetchRequests();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Deduct error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to deduct from rent", "error");
            }
        }
    };

    // Upload photo
    const handleUploadPhoto = async () => {
        if (!selectedPhoto) {
            showToast("Validation Error", "Please select a photo to upload", "warning");
            return;
        }

        setUploading(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('request_id', selectedRecord.id);
            formDataToSend.append('photo', selectedPhoto);

            const result = await apiService.create(APIMaintenanceRequestUploadPhoto, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setPhotoUploadOpen(false);
                setSelectedPhoto(null);
                fetchRequests();
            }
        } catch (error) {
            console.error("Upload error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to upload photo", "error");
            }
        } finally {
            if (isMounted.current) {
                setUploading(false);
            }
        }
    };

    // View history
    const handleViewHistory = async (record) => {
        setSelectedRecord(record);
        await fetchRequestHistory(record.id);
        setHistoryDialogOpen(true);
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            request_status: '',
            priority: '',
            property_id: '',
            floor_unit_name_id: '',
            issue_category: '',
            date_from: '',
            date_to: '',
            estimated_cost_min: '',
            estimated_cost_max: ''
        });
        setSearchTerm('');
        setSelectedProperty('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);
    const handleCloseView = () => setOpenView(false);
    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    // Dialog handlers
    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add maintenance requests", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            floor_unit_name_id: '',
            tenant_id: '',
            property_id: '',
            floor_name: '',
            apartment_number: '',
            issue_category: '',
            issue_description: '',
            priority: 'medium',
            reported_date: new Date().toISOString().slice(0, 16),
            assigned_to: '',
            estimated_cost: '',
            actual_cost: '',
            deducted_from_rent: false,
            request_status: 'reported',
            notes: '',
            preferred_date: '',
            preferred_time: ''
        });
        setPropertySearchInput('');
        setApartmentSearchInput('');
        setErrors({
            floor_unit_name_id: false,
            issue_description: false,
            reported_date: false
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit maintenance requests", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            floor_unit_name_id: record.floor_unit_name_id || '',
            tenant_id: record.tenant_id || '',
            property_id: record.property_id || '',
            floor_name: record.floor_name || '',
            apartment_number: record.apartment_number || '',
            issue_category: record.issue_category || '',
            issue_description: record.issue_description || '',
            priority: record.priority || 'medium',
            reported_date: record.reported_date || new Date().toISOString().slice(0, 16),
            assigned_to: record.assigned_to || '',
            estimated_cost: record.estimated_cost || '',
            actual_cost: record.actual_cost || '',
            deducted_from_rent: record.deducted_from_rent || false,
            request_status: record.request_status || 'reported',
            notes: record.notes || '',
            preferred_date: record.preferred_date || '',
            preferred_time: record.preferred_time || ''
        });

        // Set search inputs for selected values
        const selectedProperty = properties.find(p => p.id === record.property_id);
        const selectedApartment = apartments.find(a => a.id === record.floor_unit_name_id);
        
        setPropertySearchInput(selectedProperty ? selectedProperty.property_name : '');
        setApartmentSearchInput(selectedApartment ? `${selectedApartment.floor_name} - ${selectedApartment.unit_name}` : '');

        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete maintenance requests", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleOpenAssign = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to assign requests", "error");
            return;
        }
        setSelectedRecord(record);
        setAssignedTo(record.assigned_to || '');
        setAssignDialogOpen(true);
    };

    const handleOpenStatusUpdate = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update status", "error");
            return;
        }
        setSelectedRecord(record);
        setNewStatus(record.request_status);
        setStatusUpdateDialogOpen(true);
    };

    const handleOpenComplete = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to complete requests", "error");
            return;
        }
        setSelectedRecord(record);
        setActualCost(record.actual_cost || '');
        setCompletionNotes('');
        setCompleteDialogOpen(true);
    };

    const handleOpenDeduct = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to deduct from rent", "error");
            return;
        }
        setSelectedRecord(record);
        setDeductAmount(record.actual_cost || '');
        setDeductNotes('');
        setDeductDialogOpen(true);
    };

    const handleOpenPhotoUpload = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to upload photos", "error");
            return;
        }
        setSelectedRecord(record);
        setSelectedPhoto(null);
        setPhotoUploadOpen(true);
    };

    // Property selection handler
    const handlePropertySelect = useCallback((event, selectedProperty) => {
        const propertyId = selectedProperty ? selectedProperty.id : '';
        setFormData(prev => ({
            ...prev,
            property_id: propertyId,
            floor_unit_name_id: '' // Reset apartment when property changes
        }));
        setApartmentSearchInput(''); // Reset apartment search input
        if (propertyId) {
            fetchApartments(propertyId);
        } else {
            fetchApartments(null);
        }
    }, [fetchApartments]);

    // Apartment selection handler
    const handleApartmentSelect = useCallback((event, selectedApartment) => {
        if (selectedApartment) {
            setFormData(prev => ({
                ...prev,
                floor_unit_name_id: selectedApartment.id,
                property_id: selectedApartment.property_id || prev.property_id,
                floor_name: selectedApartment.floor_name || '',
                apartment_number: selectedApartment.unit_name || ''
            }));
            // Update property search input if property is set from apartment selection
            if (selectedApartment.property_id) {
                const property = properties.find(p => p.id === selectedApartment.property_id);
                if (property) {
                    setPropertySearchInput(property.property_name);
                }
            }
        } else {
            setFormData(prev => ({
                ...prev,
                floor_unit_name_id: '',
                floor_name: '',
                apartment_number: ''
            }));
        }
    }, [properties]);

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateForm = () => {
        if (!formData.floor_unit_name_id) {
            showToast("Validation Error", "Please select an apartment", "warning");
            return false;
        }
        if (!formData.issue_description?.trim()) {
            showToast("Validation Error", "Issue description is required", "warning");
            return false;
        }
        if (!formData.reported_date) {
            showToast("Validation Error", "Reported date is required", "warning");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add maintenance requests", "error");
            return;
        }
        
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update maintenance requests", "error");
            return;
        }
        
        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete maintenance requests", "error");
            return;
        }
        
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('floor_unit_name_id', formData.floor_unit_name_id);
            formDataToSend.append('tenant_id', formData.tenant_id || '');
            formDataToSend.append('issue_category', formData.issue_category);
            formDataToSend.append('issue_description', formData.issue_description);
            formDataToSend.append('priority', formData.priority);
            formDataToSend.append('reported_date', formData.reported_date);
            formDataToSend.append('assigned_to', formData.assigned_to || '');
            formDataToSend.append('estimated_cost', formData.estimated_cost || 0);
            formDataToSend.append('request_status', formData.request_status);
            formDataToSend.append('notes', formData.notes || '');
            formDataToSend.append('preferred_date', formData.preferred_date || '');
            formDataToSend.append('preferred_time', formData.preferred_time || '');
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APIMaintenanceRequestStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APIMaintenanceRequestUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APIMaintenanceRequestDelete, formDataToSend);
            }

            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchRequests();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
            if (isMounted.current) {
                showToast("Error", error.response?.data?.message || "Operation failed", "error");
            }
        }
    };

    const getStatusColor = (status) => {
        const option = requestStatusOptions.find(opt => opt.id === status);
        return option?.color || 'default';
    };

    const getStatusIcon = (status) => {
        const option = requestStatusOptions.find(opt => opt.id === status);
        return option?.icon || <Pending />;
    };

    const getStatusText = (status) => {
        const option = requestStatusOptions.find(opt => opt.id === status);
        return option ? option.name : status;
    };

    const getPriorityColor = (priority) => {
        const option = priorityLevels.find(opt => opt.id === priority);
        return option?.color || 'default';
    };

    const getPriorityIcon = (priority) => {
        const option = priorityLevels.find(opt => opt.id === priority);
        return option?.icon || <TrendingFlat />;
    };

    const getPriorityText = (priority) => {
        const option = priorityLevels.find(opt => opt.id === priority);
        return option ? option.name : priority;
    };

    const getCategoryIcon = (category) => {
        const option = issueCategories.find(opt => opt.id === category);
        return option?.icon || <Handyman />;
    };

    const getCategoryName = (category) => {
        const option = issueCategories.find(opt => opt.id === category);
        return option ? option.name : category;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    // Filtered data
    const filteredData = useMemo(() => {
        return pageState.pageData.filter(record => {
            const matchesSearch = searchTerm === '' ||
                record.apartment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.issue_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.assigned_to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.request_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.property_name?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = !filters.request_status || 
                record.request_status === filters.request_status;

            const matchesPriority = !filters.priority || 
                record.priority === filters.priority;

            const matchesProperty = !filters.property_id || 
                record.property_id?.toString() === filters.property_id;

            const matchesApartment = !filters.floor_unit_name_id || 
                record.floor_unit_name_id?.toString() === filters.floor_unit_name_id;

            const matchesCategory = !filters.issue_category || 
                record.issue_category === filters.issue_category;

            let matchesDate = true;
            if (filters.date_from && record.reported_date) {
                matchesDate = matchesDate && record.reported_date >= filters.date_from;
            }
            if (filters.date_to && record.reported_date) {
                matchesDate = matchesDate && record.reported_date <= filters.date_to;
            }

            const matchesMinCost = !filters.estimated_cost_min || 
                (record.estimated_cost && record.estimated_cost >= parseFloat(filters.estimated_cost_min));
            const matchesMaxCost = !filters.estimated_cost_max || 
                (record.estimated_cost && record.estimated_cost <= parseFloat(filters.estimated_cost_max));

            return matchesSearch && matchesStatus && matchesPriority && 
                   matchesProperty && matchesApartment && matchesCategory && 
                   matchesDate && matchesMinCost && matchesMaxCost;
        });
    }, [pageState.pageData, searchTerm, filters]);

    // Pagination
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return <LoadingState message="Loading Maintenance Requests..." variant="medical" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchRequests} title="Unable to Load Maintenance Data" variant="medical" fullPage retryText="Retry Loading" />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Requests</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        {statistics.total_requests || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                    <Handyman sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Open Requests</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                        {statistics.open_requests || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                    <Pending sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Completed</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                        {statistics.completed_requests || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                    <CheckCircle sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Cost</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                        {formatCurrency(statistics.total_cost || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                    <AttachMoney sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Action Buttons */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, flexShrink: 0 }}>
                        Maintenance Requests ({recordsCount(pageState.pageData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                New Request
                            </Button>
                        )}
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                View Options
                            </Button>
                        )}
                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Export Data
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => { fetchRequests(); fetchStatistics(); }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search requests..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': { color: 'white', '&::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>
            </Paper>

            {/* Export Menu */}
            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}><TableChart fontSize="small" color="success" /> Export to Excel (.xlsx)</MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}><PictureAsPdf fontSize="small" color="error" /> Export to PDF (.pdf)</MenuItem>
            </Menu>

            {/* Requests Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.primary.main, '& th': { color: 'white', fontWeight: 'bold', fontSize: '0.95rem' } }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Property/Apartment</TableCell>
                                    <TableCell>Issue</TableCell>
                                    <TableCell>Priority</TableCell>
                                    <TableCell>Reported Date</TableCell>
                                    <TableCell>Assigned To</TableCell>
                                    <TableCell>Est. Cost</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                    <TableRow key={record.id || index} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                        <TableCell><Chip label={page * rowsPerPage + index + 1} color="primary" size="small" /></TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{record.property_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">Apt: {record.apartment_number} (Floor {record.floor_name})</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={record.issue_description}>
                                                <Box>
                                                    <Chip icon={getCategoryIcon(record.issue_category)} label={getCategoryName(record.issue_category)} size="small" variant="outlined" />
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getPriorityIcon(record.priority)}
                                                label={getPriorityText(record.priority)}
                                                color={getPriorityColor(record.priority)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{new Date(record.reported_date).toLocaleDateString()}</TableCell>
                                        <TableCell>{record.assigned_to || '-'}</TableCell>
                                        <TableCell>
                                            {record.estimated_cost > 0 ? formatCurrency(record.estimated_cost) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getStatusIcon(record.request_status)}
                                                label={getStatusText(record.request_status)}
                                                color={getStatusColor(record.request_status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {hasPermission('VIEW') && (
                                                <Tooltip title="View Details"><IconButton onClick={() => handleOpenView(record)} color="primary"><ViewIcon /></IconButton></Tooltip>
                                            )}
                                            {hasPermission('PRINT') && (
                                                <Tooltip title="Print"><IconButton onClick={() => handlePrintRequest(record)} color="info"><Print /></IconButton></Tooltip>
                                            )}
                                            {hasPermission('EDIT') && (
                                                <>
                                                    <Tooltip title="Edit"><IconButton onClick={() => handleOpenEdit(record)} color="secondary"><EditIcon /></IconButton></Tooltip>
                                                    <Tooltip title="Update Status"><IconButton onClick={() => handleOpenStatusUpdate(record)} color="warning"><TrendingFlat /></IconButton></Tooltip>
                                                    {!record.assigned_to && record.request_status === 'reported' && (
                                                        <Tooltip title="Assign"><IconButton onClick={() => handleOpenAssign(record)} color="info"><AssignmentInd /></IconButton></Tooltip>
                                                    )}
                                                    {record.request_status !== 'completed' && record.request_status !== 'cancelled' && (
                                                        <Tooltip title="Mark Complete"><IconButton onClick={() => handleOpenComplete(record)} color="success"><CheckCircle /></IconButton></Tooltip>
                                                    )}
                                                    {record.completed && !record.deducted_from_rent && record.actual_cost > 0 && (
                                                        <Tooltip title="Deduct from Rent"><IconButton onClick={() => handleOpenDeduct(record)} color="error"><MoneyOff /></IconButton></Tooltip>
                                                    )}
                                                    <Tooltip title="Upload Photo"><IconButton onClick={() => handleOpenPhotoUpload(record)} color="primary"><CloudUpload /></IconButton></Tooltip>
                                                    <Tooltip title="View History"><IconButton onClick={() => handleViewHistory(record)} color="info"><History /></IconButton></Tooltip>
                                                </>
                                            )}
                                            {hasPermission('DELETE') && (
                                                <Tooltip title="Delete"><IconButton onClick={() => handleOpenDelete(record)} color="error"><Delete /></IconButton></Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100]} component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ mt: 2 }} />
                    {filteredData.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                            <Handyman sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No maintenance requests found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Dialog - Prevent click outside */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={clearFilters}>Show All Requests</Button>
                        <Divider>OR</Divider>
                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>Filter Requests</Typography>
                        <Box>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Request Status</InputLabel>
                                    <Select name="request_status" value={filters.request_status} onChange={handleFilterChange} label="Request Status">
                                        <MenuItem value="">All Status</MenuItem>
                                        {requestStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Priority</InputLabel>
                                    <Select name="priority" value={filters.priority} onChange={handleFilterChange} label="Priority">
                                        <MenuItem value="">All Priorities</MenuItem>
                                        {priorityLevels.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Issue Category</InputLabel>
                                    <Select name="issue_category" value={filters.issue_category} onChange={handleFilterChange} label="Issue Category">
                                        <MenuItem value="">All Categories</MenuItem>
                                        {issueCategories.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Property</InputLabel>
                                    <Select name="property_id" value={filters.property_id} onChange={handleFilterChange} label="Property">
                                        <MenuItem value="">All Properties</MenuItem>
                                        {properties.map(property => <MenuItem key={property.id} value={property.id}>{property.property_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date From" name="date_from" type="date" value={filters.date_from} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date To" name="date_to" type="date" value={filters.date_to} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Min Est. Cost" name="estimated_cost_min" type="number" value={filters.estimated_cost_min} onChange={handleFilterChange} placeholder="Min cost" />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Max Est. Cost" name="estimated_cost_max" type="number" value={filters.estimated_cost_max} onChange={handleFilterChange} placeholder="Max cost" />
                            </Grid>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">Close</Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">Apply Filters</Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Request Dialog - Prevent click outside */}
            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdd();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>{postStatus === "ADD" ? "New Maintenance Request" : "Edit Maintenance Request"}</b></DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>Location Information</Typography>
                            </Grid>
                            <br/>

                                {/* Property Autocomplete */}
                                <Grid item xs={12}>
                                <Autocomplete
                                    id="property-select"
                                    options={properties}
                                    value={properties.find(p => p.id === formData.property_id) || null}
                                    onChange={handlePropertySelect}
                                    inputValue={propertySearchInput}
                                    onInputChange={(event, newInputValue) => {
                                        setPropertySearchInput(newInputValue);
                                    }}
                                    getOptionLabel={(option) => option.property_name}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body1">
                                                    {option.property_name}
                                                </Typography>
                                                {option.location && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Location: {option.location}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search Property *"
                                            placeholder="Type property name to search..."
                                            required
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start">
                                                            <Business />
                                                        </InputAdornment>
                                                        {params.InputProps?.startAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <br/>

                            {/* Floor Unit Name Autocomplete - filters based on selected property */}
                            <Grid item xs={12}>
                                <Autocomplete
                                    id="apartment-select"
                                    options={apartments.filter(apt => 
                                        !formData.property_id || apt.property_id === formData.property_id
                                    )}
                                    value={apartments.find(a => a.id === formData.floor_unit_name_id) || null}
                                    onChange={handleApartmentSelect}
                                    inputValue={apartmentSearchInput}
                                    onInputChange={(event, newInputValue) => {
                                        setApartmentSearchInput(newInputValue);
                                    }}
                                    getOptionLabel={(option) => `${option.floor_name} - ${option.unit_name}`}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body1">
                                                    Floor {option.floor_name} - Unit {option.unit_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Property: {option.property_name}
                                                </Typography>
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Floor - Unit Name *"
                                            placeholder="Search floor and unit name..."
                                            required
                                            error={errors.floor_unit_name_id}
                                            helperText={errors.floor_unit_name_id ? "Floor and Unit Name is required" : "Select floor and unit name"}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start">
                                                            <Home />
                                                        </InputAdornment>
                                                        {params.InputProps?.startAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1, mt: 2 }}>Issue Details</Typography>
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Issue Category</InputLabel>
                                    <Select name="issue_category" value={formData.issue_category} onChange={handleInputChange} label="Issue Category">
                                        <MenuItem value="">Select Category</MenuItem>
                                        {issueCategories.map(item => <MenuItem key={item.id} value={item.id}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{item.icon} {item.name}</Box></MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Priority</InputLabel>
                                    <Select name="priority" value={formData.priority} onChange={handleInputChange} label="Priority">
                                        {priorityLevels.map(item => <MenuItem key={item.id} value={item.id}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{item.icon} {item.name}</Box></MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Issue Description *"
                                    name="issue_description"
                                    value={formData.issue_description}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={4}
                                    required
                                    error={errors.issue_description}
                                    helperText={errors.issue_description ? "Issue description is required" : "Describe the maintenance issue in detail..."}
                                    placeholder="Describe the maintenance issue in detail..."
                                />
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1, mt: 2 }}>Schedule Information</Typography>
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <TextField fullWidth label="Reported Date *" name="reported_date" type="datetime-local" value={formData.reported_date} onChange={handleInputChange} required InputLabelProps={{ shrink: true }} error={errors.reported_date} helperText={errors.reported_date ? "Reported date is required" : ""} />
                            </Grid>
                            <br/>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Preferred Date" name="preferred_date" type="date" value={formData.preferred_date} onChange={handleInputChange} InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Preferred Time" name="preferred_time" type="time" value={formData.preferred_time} onChange={handleInputChange} InputLabelProps={{ shrink: true }} />
                                </Grid>
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1, mt: 2 }}>Cost & Assignment</Typography>
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <TextField fullWidth label="Assigned To" name="assigned_to" value={formData.assigned_to} onChange={handleInputChange} placeholder="Vendor or staff name" InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }} />
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <TextField fullWidth label="Estimated Cost (UGX)" name="estimated_cost" type="number" value={formData.estimated_cost} onChange={handleInputChange} InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment> }} />
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Request Status</InputLabel>
                                    <Select name="request_status" value={formData.request_status} onChange={handleInputChange} label="Request Status">
                                        {requestStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={handleInputChange} multiline rows={3} placeholder="Additional notes..." InputProps={{ startAdornment: <InputAdornment position="start"><Note /></InputAdornment> }} />
                            </Grid>
                        </Box>
                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">Cancel</Button>
                            <Button type="submit" color="primary" variant="contained">{postStatus === "ADD" ? "Create Request" : "Update Request"}</Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Assign Dialog - Prevent click outside */}
            <Dialog
                open={assignDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setAssignDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Assign Maintenance Request</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body1" gutterBottom>Assign request for <strong>{selectedRecord?.apartment_number}</strong></Typography>
                        <TextField fullWidth label="Assigned To" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} placeholder="Enter vendor or staff name" sx={{ mt: 2 }} InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setAssignDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleAssign} color="primary" variant="contained">Assign</Button>
                </DialogActions>
            </Dialog>

            {/* Update Status Dialog - Prevent click outside */}
            <Dialog
                open={statusUpdateDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setStatusUpdateDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Update Request Status</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body1" gutterBottom>Update status for <strong>{selectedRecord?.request_id}</strong></Typography>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>New Status</InputLabel>
                            <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} label="New Status">
                                {requestStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setStatusUpdateDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleUpdateStatus} color="primary" variant="contained">Update Status</Button>
                </DialogActions>
            </Dialog>

            {/* Complete Dialog - Prevent click outside */}
            <Dialog
                open={completeDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setCompleteDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Complete Maintenance Request</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body1" gutterBottom>Complete request for <strong>{selectedRecord?.apartment_number}</strong></Typography>
                        <TextField fullWidth label="Actual Cost (UGX)" type="number" value={actualCost} onChange={(e) => setActualCost(e.target.value)} sx={{ mt: 2 }} InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment> }} />
                        <TextField fullWidth label="Completion Notes" value={completionNotes} onChange={(e) => setCompletionNotes(e.target.value)} multiline rows={3} sx={{ mt: 2 }} placeholder="Notes about completed work..." InputProps={{ startAdornment: <InputAdornment position="start"><Description /></InputAdornment> }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setCompleteDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleComplete} color="success" variant="contained">Mark Complete</Button>
                </DialogActions>
            </Dialog>

            {/* Deduct from Rent Dialog - Prevent click outside */}
            <Dialog
                open={deductDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setDeductDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Deduct from Rent</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>This will deduct the amount from the tenant's next rent invoice.</Alert>
                        <Typography variant="body1" gutterBottom>Deduct from <strong>{selectedRecord?.tenant_name || 'Tenant'}</strong>'s rent</Typography>
                        <TextField fullWidth label="Amount to Deduct (UGX)" type="number" value={deductAmount} onChange={(e) => setDeductAmount(e.target.value)} sx={{ mt: 2 }} InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment> }} />
                        <TextField fullWidth label="Notes" value={deductNotes} onChange={(e) => setDeductNotes(e.target.value)} multiline rows={2} sx={{ mt: 2 }} placeholder="Reason for deduction..." InputProps={{ startAdornment: <InputAdornment position="start"><Note /></InputAdornment> }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setDeductDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleDeductFromRent} color="warning" variant="contained">Confirm Deduction</Button>
                </DialogActions>
            </Dialog>

            {/* Photo Upload Dialog - Prevent click outside */}
            <Dialog
                open={photoUploadOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setPhotoUploadOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Upload Photo</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth>
                            Select Photo
                            <input type="file" hidden accept="image/*" onChange={(e) => setSelectedPhoto(e.target.files[0])} />
                        </Button>
                        {selectedPhoto && (
                            <Typography variant="caption" display="block" color="success.main" sx={{ mt: 1 }}>
                                Selected: {selectedPhoto.name}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setPhotoUploadOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleUploadPhoto} color="primary" variant="contained" disabled={uploading}>
                        {uploading ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog - Prevent click outside */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Maintenance Request Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Box>
                                <Grid item xs={12}><Typography variant="h6" color="primary">Request Information</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Request ID:</b> {selectedRecord.request_id}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Status:</b> <Chip icon={getStatusIcon(selectedRecord.request_status)} label={getStatusText(selectedRecord.request_status)} color={getStatusColor(selectedRecord.request_status)} size="small" /></Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Priority:</b> <Chip icon={getPriorityIcon(selectedRecord.priority)} label={getPriorityText(selectedRecord.priority)} color={getPriorityColor(selectedRecord.priority)} size="small" /></Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Category:</b> <Chip icon={getCategoryIcon(selectedRecord.issue_category)} label={getCategoryName(selectedRecord.issue_category)} size="small" variant="outlined" /></Typography></Grid><br/>
                                <Grid item xs={12}><Typography variant="subtitle2"><b>Issue Description:</b></Typography><Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f5f5f5' }}><Typography variant="body2">{selectedRecord.issue_description}</Typography></Paper></Grid><br/>
                                <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ mt: 2 }}>Location & Assignment</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Property:</b> {selectedRecord.property_name}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Apartment:</b> {selectedRecord.apartment_number} (Floor {selectedRecord.floor_name})</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Assigned To:</b> {selectedRecord.assigned_to || 'Not assigned'}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Reported By:</b> {selectedRecord.reported_by_name || 'System'}</Typography></Grid><br/>
                                <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ mt: 2 }}>Schedule & Cost</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Reported Date:</b> {new Date(selectedRecord.reported_date).toLocaleString()}</Typography></Grid><br/>
                                {selectedRecord.preferred_date && <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Preferred Date:</b> {selectedRecord.preferred_date} {selectedRecord.preferred_time}</Typography></Grid>}<br/>
                                {selectedRecord.completion_date && <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Completion Date:</b> {new Date(selectedRecord.completion_date).toLocaleString()}</Typography></Grid>}<br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Estimated Cost:</b> {formatCurrency(selectedRecord.estimated_cost)}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Actual Cost:</b> {formatCurrency(selectedRecord.actual_cost)}</Typography></Grid><br/>
                                {selectedRecord.deducted_from_rent && <Grid item xs={12}><Typography variant="subtitle2"><b>Deducted from Rent:</b> Yes</Typography></Grid>}<br/>
                                {selectedRecord.notes && <Grid item xs={12}><Typography variant="subtitle2"><b>Notes:</b> {selectedRecord.notes}</Typography></Grid>}<br/>
                                {selectedRecord.completion_notes && <Grid item xs={12}><Typography variant="subtitle2"><b>Completion Notes:</b> {selectedRecord.completion_notes}</Typography></Grid>}<br/>
                                <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ mt: 2 }}>System Information</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Created:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography></Grid><br/>
                            </Box>
                            {selectedRecord.photos && selectedRecord.photos.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2"><b>Photos:</b></Typography>
                                    <Grid container spacing={1} sx={{ mt: 1 }}>
                                        {selectedRecord.photos.map((photo, idx) => (
                                            <Grid item key={idx}>
                                                <img src={photo} alt={`Maintenance photo ${idx + 1}`} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">Close</Button>
                    {hasPermission('PRINT') && selectedRecord && <Button onClick={() => handlePrintRequest(selectedRecord)} color="info" variant="contained"><Print sx={{ mr: 1 }} /> Print</Button>}
                </DialogActions>
            </Dialog>

            {/* History Dialog - Prevent click outside */}
            <Dialog
                open={historyDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setHistoryDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Request History</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        {requestHistory.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No history records found</Typography>
                        ) : (
                            <Box>
                                {requestHistory.map((event, index) => (
                                    <Paper key={index} elevation={1} sx={{ p: 2, mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{event.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{new Date(event.created_at).toLocaleString()}</Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">{event.description}</Typography>
                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>By: {event.performed_by_name || 'System'}</Typography>
                                    </Paper>
                                ))}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions><Button color="error" onClick={() => setHistoryDialogOpen(false)} variant="contained">Close</Button></DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog - Prevent click outside */}
            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDelete();
                    }
                }}
                TransitionComponent={Transition}
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Maintenance Request</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>Are you sure you want to delete this maintenance request?</Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center' }}>
                            {selectedRecord.request_id} - {selectedRecord.apartment_number}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Issue: {selectedRecord.issue_description?.substring(0, 100)}...
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    icon={getStatusIcon(selectedRecord.request_status)}
                                    label={getStatusText(selectedRecord.request_status)}
                                    color={getStatusColor(selectedRecord.request_status)}
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    icon={getPriorityIcon(selectedRecord.priority)}
                                    label={getPriorityText(selectedRecord.priority)}
                                    color={getPriorityColor(selectedRecord.priority)}
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>Warning: This action cannot be undone!</Typography>
                </DialogContent>
                <DialogActions><Button color="error" onClick={handleCloseDelete} variant="contained">Cancel</Button><Button onClick={handleSubmit} color="primary" variant="contained">Confirm Delete</Button></DialogActions>
            </Dialog>
        </Container>
    );
};

export default MaintenanceRequest;
